import { createContext, useContext, useEffect, useState } from 'react';

/*
  Настройки вида «Основы»: их меняют и переключатели над страницей, и раздел «Настройки › Вид»
  внутри — это одно и то же состояние.
*/

export interface FoundationPrefs {
  /** Тон второго уровня: продолжение меню или в цвет страницы. */
  sectionTone: 'dark' | 'light';
  /** Плотные списки и таблицы. */
  dense: boolean;
  /** Режим фокуса: меню спрятано, остаются вкладки и страница. */
  focus: boolean;
  /**
   * Окраска по состоянию системы (R38): выделение главного меню и, у помощника, ядро в знаке. Само состояние —
   * у помощника (observer.state, стопка долгих состояний), а не здесь.
   */
  colorByState: boolean;
  /** Закреплено ли приложение-образец в главном меню. */
  pinnedInfra: boolean;
  /**
   * Подключённые сервисы (id модулей) в порядке подключения — образец. Настоящий список и порядок —
   * данные ядра (docs/core-contract.md).
   */
  services: string[];
  /** Подключены, но выключены: не шлют событий, настройки сохраняются. */
  servicesOff: string[];
  /** Сбой у Slack — образец сбоя на веере, в Marketplace и на главной. */
  serviceAlert: boolean;
  /** Что главная уже показала: остальное приезжает строкой (spec-1 §3.3). */
  seenServices: string[];
  /** Что уже показал веер «Мои сервисы»: остальное выходит пластиной. У каждой поверхности своё «показано». */
  fanSeen: string[];
  /**
   * Образец состояния вкладки (R42): активная вкладка окна в фокусе показывает «не найдено» или «нет
   * доступа» вместо своего вида; содержимое при этом живёт и вернётся как было.
   */
  tabDemo: TabDemo;
  /**
   * Темп загрузки образца (R42): «Быстро» — как раньше; «Медленно» — холодное открытие приложения по
   * 600 мс на шаг и данные видов за 1,6 с; «Сбой» — открытие встаёт на шаге «Меню», «Повторить» проходит.
   */
  loadPace: LoadPace;
  /** «Уронить вкладку»: вкладка по этому адресу бросит исключение при отрисовке — один раз. */
  crashAt: string | null;
  /**
   * Нет связи с сервером (R42): строка под полосой вкладок; первая загрузка стоит на достигнутом шаге, пока
   * связь не вернётся. Знак в меню о связи ничего не показывает (решение 21.10).
   */
  offline: boolean;
  /**
   * «Первая загрузка»: когда нажали (часы performance.now()) — сцена первой загрузки сайта проигрывается поверх
   * оболочки с этого момента. Каждое новое значение — новый прогон.
   */
  bootAt: number | null;
}

export type TabDemo = 'as-is' | 'missing' | 'locked';
export type LoadPace = 'fast' | 'slow' | 'fail';

/** Сколько едут данные вида-образца: «Быстро» — как раньше, медленнее — 1,6 с. */
export const VIEW_LOAD_MS: Record<LoadPace, number> = { fast: 700, slow: 1600, fail: 1600 };

/** Правка настроек: часть полей или функция от текущих — когда важен актуальный список (отмена в тосте). */
export type PrefsPatch = Partial<FoundationPrefs> | ((p: FoundationPrefs) => Partial<FoundationPrefs>);

export interface FoundationPrefsApi extends FoundationPrefs {
  set: (patch: PrefsPatch) => void;
}

export const PrefsContext = createContext<FoundationPrefsApi | null>(null);

export function usePrefs(): FoundationPrefsApi {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error('usePrefs: нужен PrefsContext выше по дереву');
  return ctx;
}

/**
 * Данные вида ещё едут — на их месте заглушка той же формы (R31). Темп берётся при входе в вид: смена темпа
 * на верхней полосе не перезапускает уже открытый вид.
 */
export function useViewLoading(): boolean {
  const { loadPace } = usePrefs();
  const [ms] = useState(() => VIEW_LOAD_MS[loadPace]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = window.setTimeout(() => setLoading(false), ms);
    return () => window.clearTimeout(t);
  }, [ms]);
  return loading;
}
